import { forwardRef, RefObject } from 'react';
import classnames from 'classnames';

import { ModalHeaderProps } from '@hh.ru/magritte-ui-modal/types';
import { Title } from '@hh.ru/magritte-ui-title';

import styles from './modal-header.less';

export const ModalHeader = forwardRef<
    HTMLDivElement,
    ModalHeaderProps & { labelId: string; wrapperRef: RefObject<HTMLDivElement> }
>(
    (
        {
            title,
            headerImage,
            headerHeight,
            titleAlignment,
            titleElement,
            titleMaxLines,
            titleSize,
            titleDescription,
            titleDescriptionMaxLines,
            titleStyle,
            titleDescriptionStyle,
            options,
            actions: _actions,
            actionLink,
            labelId,
            wrapperRef,
        },
        ref
    ) => {
        const actions = Array.isArray(_actions) ? _actions.slice(0, 5) : _actions;
        if (!title && !headerImage && !actions && !actionLink && !options) {
            return null;
        }
        const withTitle = title && titleElement;
        const withActions = !!actions || !!actionLink;
        const currentTitleStyle = titleStyle || (headerImage ? 'contrast' : 'primary');
        const currentTitleDescriptionStyle = titleDescriptionStyle || (headerImage ? 'contrast' : 'primary');
        const withContent = title || headerImage || actionLink || actions;

        return (
            <div
                data-qa="modal-header"
                ref={wrapperRef}
                className={styles.wrapper}
                style={headerImage ? { marginBottom: 24 } : {}}
            >
                {withContent && (
                    <div
                        className={classnames(styles.content, {
                            [styles.contentWithImage]: !!headerImage,
                        })}
                        style={headerImage ? { height: headerHeight } : {}}
                        ref={ref}
                        data-qa="modal-header-image"
                    >
                        {headerImage && (
                            <div
                                className={styles.imageContainer}
                                style={{
                                    backgroundImage: `${
                                        title ? 'var(--modal-header-gradient), ' : ''
                                    }url(${headerImage})`,
                                    height: headerHeight,
                                }}
                            />
                        )}
                        {withTitle && (
                            <div className={styles.titleWrapper}>
                                <Title
                                    id={labelId}
                                    Element={titleElement}
                                    alignment={titleAlignment}
                                    maxLines={titleMaxLines}
                                    size={titleSize}
                                    style={currentTitleStyle}
                                    description={titleDescription}
                                    descriptionMaxLines={titleDescriptionMaxLines}
                                    descriptionStyle={currentTitleDescriptionStyle}
                                >
                                    {title}
                                </Title>
                            </div>
                        )}
                        {withActions && (
                            <div
                                className={classnames(styles.actions, {
                                    [styles.actionsWithLink]: !actions && !!actionLink,
                                    [styles.actionsSingle]: Array.isArray(actions) ? actions.length === 1 : !!actions,
                                })}
                            >
                                {actions || actionLink}
                            </div>
                        )}
                    </div>
                )}
                {options && <div className={styles.options}>{options}</div>}
            </div>
        );
    }
);

ModalHeader.displayName = 'ModalHeader';
